<template>
  <gs-modal
    v-model="visible"
    class="gs-checkout-link-modal"
    title="Checkout link"
    subtitle="Copy the hosted link, embed code, or popup code for this checkout experience."
    size="lg"
  >
    <div class="gs-checkout-link-modal__content">
      <p class="gs-checkout-link-modal__name">{{ checkoutName }}</p>

      <div class="gs-checkout-link-modal__tabs" role="tablist">
        <button v-for="tab in tabs" :key="tab.value" type="button" role="tab"
          :aria-selected="currentTab === tab.value" :class="{ 'is-active': currentTab === tab.value }"
          @click="currentTab = tab.value">{{ tab.label }}</button>
      </div>

      <section v-if="currentTab === 'link'" class="gs-checkout-link-modal__panel">
        <label class="gs-checkout-link-modal__label" for="gs-checkout-link-hosted">Hosted checkout link</label>
        <input
          id="gs-checkout-link-hosted"
          class="gs-checkout-link-modal__field"
          :value="hostedLink || 'No hosted checkout link is available yet.'"
          readonly
          @focus="$event.target.select()"
        />
        <div class="gs-checkout-link-modal__actions">
          <gs-button variant="outline" :disabled="!hostedLink" @click="openHosted">Open link</gs-button>
          <gs-button variant="primary" :disabled="!hostedLink" @click="copy(hostedLink)">Copy link</gs-button>
        </div>
      </section>

      <section v-else-if="currentTab === 'embed'" class="gs-checkout-link-modal__panel">
        <label class="gs-checkout-link-modal__label" for="gs-checkout-link-embed">Embed code</label>
        <textarea
          id="gs-checkout-link-embed"
          class="gs-checkout-link-modal__field"
          :value="embedCode || 'Embed code is not available for this checkout.'"
          readonly
          rows="6"
          @focus="$event.target.select()"
        ></textarea>
        <div class="gs-checkout-link-modal__actions">
          <gs-button variant="primary" :disabled="!embedCode" @click="copy(embedCode)">Copy embed code</gs-button>
        </div>
      </section>

      <section v-else class="gs-checkout-link-modal__panel">
        <label class="gs-checkout-link-modal__label" for="gs-checkout-link-popup">Popup modal code</label>
        <textarea
          id="gs-checkout-link-popup"
          class="gs-checkout-link-modal__field"
          :value="popupCode || 'Popup code is not available for this checkout.'"
          readonly
          rows="6"
          @focus="$event.target.select()"
        ></textarea>
        <div class="gs-checkout-link-modal__actions">
          <gs-button variant="primary" :disabled="!popupCode" @click="copy(popupCode)">Copy popup code</gs-button>
        </div>
      </section>
    </div>
  </gs-modal>
</template>

<script>
import Notify from '@groovepages/gd-ui-kit/notify';

export default {
  name: 'ModernCheckoutLinkModal',
  props: {
    value: { type: Boolean, default: false },
    checkoutLink: { type: Object, default: null },
    checkout_link: { type: String, default: '' },
    checkout_embed: { type: String, default: '' },
  },
  data() {
    return {
      currentTab: 'link',
      tabs: [
        { value: 'link', label: 'Checkout link' },
        { value: 'embed', label: 'Embed code' },
        { value: 'popup', label: 'Popup modal' },
      ],
    };
  },
  computed: {
    visible: {
      get() { return this.value; },
      set(value) { this.$emit('input', value); },
    },
    checkoutName() {
      return (this.checkoutLink && (this.checkoutLink.name || this.checkoutLink.title)) || 'Checkout link';
    },
    hostedLink() { return (this.checkoutLink && this.checkoutLink.checkout_link) || this.checkout_link || ''; },
    embedCode() { return (this.checkoutLink && this.checkoutLink.checkout_embed) || this.checkout_embed || ''; },
    popupCode() {
      return (this.checkoutLink && (this.checkoutLink.checkout_popup || this.checkoutLink.popup_code)) || '';
    },
  },
  watch: {
    value(value) { if (value) this.currentTab = 'link'; },
  },
  methods: {
    async copy(value) {
      if (!value || !navigator.clipboard) return;
      try {
        await navigator.clipboard.writeText(value);
        Notify.success('Copied to clipboard.');
      } catch (error) {
        Notify.error('Unable to copy.');
      }
    },
    openHosted() {
      if (this.hostedLink) window.open(this.hostedLink, '_blank');
    },
  },
};
</script>

<style>
/* The kit leaves .gs-modal__body unpadded, so each modal surface insets its own
   content. Keep the class on <gs-modal> so this rule can reach the body. */
.gs-checkout-link-modal .gs-modal__panel { width: min(100%, 680px); }
.gs-checkout-link-modal .gs-modal__body { padding: 20px 24px 24px; }

.gs-checkout-link-modal__content {
  display: grid;
  gap: 16px;
  min-width: 0;
}

.gs-checkout-link-modal__name {
  margin: 0;
  color: var(--gs-foreground, #17213f);
  font-size: 15px;
  font-weight: 700;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.gs-checkout-link-modal__tabs {
  display: flex;
  gap: 4px;
  min-width: 0;
  overflow-x: auto;
  border-bottom: 1px solid var(--gs-border, #e5e7eb);
}

.gs-checkout-link-modal__tabs button {
  flex: 0 0 auto;
  padding: 10px 12px;
  border: 0;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: var(--gs-muted-foreground, #667085);
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
}

.gs-checkout-link-modal__tabs button.is-active {
  border-bottom-color: var(--gs-active, #ef2b7b);
  color: var(--gs-foreground, #17213f);
  font-weight: 700;
}

.gs-checkout-link-modal__tabs button:focus-visible {
  outline: 2px solid var(--gs-active, #2563eb);
  outline-offset: 2px;
}

.gs-checkout-link-modal__panel {
  display: grid;
  gap: 10px;
  min-width: 0;
}

.gs-checkout-link-modal__label {
  color: var(--gs-muted-foreground, #667085);
  font-size: 13px;
  font-weight: 600;
}

.gs-checkout-link-modal__field {
  box-sizing: border-box;
  display: block;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  padding: 11px 12px;
  border: 1px solid var(--gs-border, #d9deea);
  border-radius: 8px;
  background: var(--gs-surface-subtle, #f8fafc);
  color: var(--gs-foreground, #17213f);
  font-family: var(--gs-font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
  font-size: 13px;
  line-height: 1.5;
}

textarea.gs-checkout-link-modal__field {
  min-height: 132px;
  resize: vertical;
  white-space: pre;
}

.gs-checkout-link-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}

@media (max-width: 480px) {
  .gs-checkout-link-modal .gs-modal__body { padding: 18px; }
  .gs-checkout-link-modal__actions > * { flex: 1 1 auto; }
}
</style>
